import { getMutualFundDetailCTA } from '../../utils/mutualFundDetail'
import { roundOffAndFormatNumberWithCurrency, toPascalCaseWithSpaces, isWithinDays } from '../../utils/utils'
import RenderHTML from '../ui-components/RenderHTML'
import { PowerFormBadge } from '../ui-components/PowerFormBadge'
import { mfLogoBaseUrl } from '../../env'
import { LeafRank } from '../know-why/LeafRank'
import { RightChevron } from '../../icons/questionnaire/RightChevron'
import WarningIcon from '../../icons/WarningIcon'
import { useBottomSheet } from '../../state-management/contexts/BottomSheetContext'
import BottomSheetContent from '../BottomSheetComponents/BottomSheetContent'
import { PowerPerformanceForms } from '../../types/mutual-fund-detail.types'
import PrimaryButton from '../ui-components/PrimaryButton'
import { postCTA } from '../../utils/appBridge'
import GreenCircleTick from '../../icons/GreenCircleTick'
import { RegularPlanAlert } from '../BottomSheetComponents/RegularPlanAlert'
import MetaDataProvider from '../../state-management/providers/MetaDataProvider'
import { RegularVsDirectFundValue } from '../../types/mutual-fund-detail.types'

const getSecondaryUserName = (secondaryUserName?: string) => {
  return secondaryUserName?.split(' ')?.[0] ?? ''
}

/** Orange info icon; keep with trailing word via getCommissionsInfoIconHtmlAfterWord. */
const COMMISSIONS_INFO_ICON_HTML = `<span class="relative top-[1px] inline-flex shrink-0"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" class="inline-block" aria-hidden="true">
  <g clip-path="url(#clip0_commissions_info)">
    <path d="M6 0.5C7.90893 0.5 9.26047 0.98508 10.1377 1.8623C11.0149 2.73953 11.5 4.09108 11.5 6C11.5 7.90893 11.0149 9.26047 10.1377 10.1377C9.26047 11.0149 7.90893 11.5 6 11.5C4.09108 11.5 2.73953 11.0149 1.8623 10.1377C0.98508 9.26047 0.5 7.90893 0.5 6C0.5 4.09108 0.985078 2.73953 1.8623 1.8623C2.73953 0.985078 4.09108 0.5 6 0.5Z" stroke="#CC5C2D"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M4.78274 7.65517C4.50254 7.65517 4.27539 7.87673 4.27539 8.15004C4.27539 8.42337 4.50254 8.64492 4.78274 8.64492H7.21804C7.49824 8.64492 7.72539 8.42337 7.72539 8.15004C7.72539 7.87673 7.49824 7.65517 7.21804 7.65517H6.50774V5.37873C6.50774 5.10542 6.2806 4.88386 6.00039 4.88386H5.18863C4.90842 4.88386 4.68127 5.10542 4.68127 5.37873C4.68127 5.65205 4.90842 5.87361 5.18863 5.87361H5.49304V7.65517H4.78274ZM6.81216 3.39923C6.81216 3.83652 6.44871 4.19103 6.00039 4.19103C5.55207 4.19103 5.18863 3.83652 5.18863 3.39923C5.18863 2.96193 5.55207 2.60742 6.00039 2.60742C6.44871 2.60742 6.81216 2.96193 6.81216 3.39923Z" fill="#CC5C2D"/>
  </g>
  <defs>
    <clipPath id="clip0_commissions_info">
      <rect width="12" height="12" fill="white"/>
    </clipPath>
  </defs>
</svg></span>`

/** Keeps the trailing word and info icon on the same line when copy wraps. */
function getCommissionsInfoIconHtmlAfterWord(word: string): string {
  return `<span class="whitespace-nowrap">${word} ${COMMISSIONS_INFO_ICON_HTML}</span>`
}

// Helper to check if current date is within 30 days from plan start date
const isWithin60Days = (planStartDate: number | undefined): boolean => {
  return isWithinDays(planStartDate, 60)
}

const NewTag = ({
  planStartDate,
  recommendationStatusDisplayText
}: {
  planStartDate?: number
  recommendationStatusDisplayText?: string
}) => {
  if (!isWithin60Days(planStartDate)) {
    return null
  }
  return (
    <div className='text-[#2A9C4A] font-medium inline-flex gap-1 items-center mb-1'>
      <span className='text-[6px] align-middle'>&#9679;</span>
      <span className='text-13'>{recommendationStatusDisplayText || 'New'}</span>
    </div>
  )
}

const UpdatedTag = ({
  planStartDate,
  recommendationStatusDisplayText
}: {
  planStartDate?: number
  recommendationStatusDisplayText?: string
}) => {
  if (!isWithin60Days(planStartDate) || !recommendationStatusDisplayText) {
    return null
  }
  return (
    <div className='text-blue30 font-medium inline-flex gap-1 items-center mb-1'>
      <span className='text-[6px] align-middle'>&#9679;</span>
      <span className='text-13'>{recommendationStatusDisplayText}</span>
    </div>
  )
}

const SameTag = ({
  planStartDate,
  recommendationStatusDisplayText
}: {
  planStartDate?: number
  recommendationStatusDisplayText?: string
}) => {
  if (!isWithin60Days(planStartDate) || !recommendationStatusDisplayText) {
    return null
  }
  return (
    <div className='text-[#6A6E6E] font-medium inline-flex gap-1 items-center mb-1'>
      <span className='text-[6px] align-middle'>&#9679;</span>
      <span className='text-13'>{recommendationStatusDisplayText}</span>
    </div>
  )
}

function MutualFundRecommendationStatusTag({
  recommendationStatus,
  planStartDate,
  recommendationStatusDisplayText
}: {
  recommendationStatus?: 'NEW' | 'UPDATED' | 'SAME' | 'INVALIDATED'
  planStartDate?: number
  recommendationStatusDisplayText?: string
}) {
  if (recommendationStatus === 'NEW') {
    return (
      <NewTag planStartDate={planStartDate} recommendationStatusDisplayText={recommendationStatusDisplayText} />
    )
  }
  if (recommendationStatus === 'UPDATED') {
    return (
      <UpdatedTag
        planStartDate={planStartDate}
        recommendationStatusDisplayText={recommendationStatusDisplayText}
      />
    )
  }
  if (recommendationStatus === 'SAME') {
    return (
      <SameTag planStartDate={planStartDate} recommendationStatusDisplayText={recommendationStatusDisplayText} />
    )
  }
  return null
}

interface FundData {
  isin: string
  name: string
  fundNature: string
  fundCategory: string
  purchaseMode: string
  powerRank: number
  performanceRating: PowerPerformanceForms
  mfLogoUrl: string
  missedGains: number
  highExposure: boolean
  logoSrc?: string
  title?: string
  sipAmount?: number
  investedAmount?: number
  isRegular?: boolean
  exclusionReason?: string
  showConsentBottomSheet?: boolean
  recommendationStatus?: 'NEW' | 'UPDATED' | 'SAME' | 'INVALIDATED'
  invalidationReason?: string
  rebalanceSubType?: 'INVALIDATED_WITH_RECOMMENDATION' | 'INVALIDATED_UNDER_REVIEW'
  recommendationStatusDisplayText?: string
}

interface MutualFundListProps {
  isin: string
  name: string
  fundNature: string
  fundCategory: string
  purchaseMode: string
  powerRank: number
  performanceRating: PowerPerformanceForms
  missedGains: number
  highExposure: boolean
  mfLogoUrl: string
  title?: string
  formType: PowerPerformanceForms
  sipAmount: number
  investedAmount?: number
  isRegular: boolean
  exclusionReason?: string
  showConsentBottomSheet?: boolean
  onClick?: () => void
  riskProfile: string | null
  borderRequired?: boolean
  isLast?: boolean
  recommendationStatus?: 'NEW' | 'UPDATED' | 'SAME' | 'INVALIDATED'
  invalidationReason?: string
  planStartDate?: number
  recommendationStatusDisplayText?: string
  additionalCommissionsPaid?: number
  regularToDirectComparison?: RegularVsDirectFundValue
  directFundName?: string
  directFundISIN?: string
  throughFamilyAccess?: boolean
  secondaryUserName?: string
}

interface MutualFundListGroupProps {
  rebalancedFunds?: FundData[] // Made optional to align with default props
  fundsExcludedFromRebalancing?: FundData[] // Made optional
  rebalanceExecutedFunds?: FundData[] // Made optional
  invalidatedRebalanceFunds?: FundData[] // Made optional
  navigate?: (path: string) => void
  riskProfile: string | null
  planStartDate?: number
  throughFamilyAccess?: boolean
  secondaryUserName?: string
}

export const MutualFundListItem = ({
  isin,
  name,
  fundNature,
  fundCategory,
  purchaseMode,
  powerRank,
  performanceRating,
  missedGains,
  highExposure,
  mfLogoUrl,
  title,
  sipAmount,
  investedAmount,
  isRegular,
  exclusionReason,
  showConsentBottomSheet,
  onClick,
  riskProfile,
  borderRequired = true,
  isLast = false,
  recommendationStatus,
  invalidationReason,
  planStartDate,
  recommendationStatusDisplayText,
  additionalCommissionsPaid,
  regularToDirectComparison,
  directFundName,
  directFundISIN,
  throughFamilyAccess,
  secondaryUserName
}: MutualFundListProps) => {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent triggering the parent onClick
    // Use directFundISIN if available, otherwise fall back to regular fund isin
    const isinToUse = directFundISIN || isin
    openBottomSheet(
      <MetaDataProvider isin={isinToUse} from='OTHER'>
        <RegularPlanAlert
          onDismiss={closeBottomSheet}
          ScreenName='MutualFundListItem'
        />
      </MetaDataProvider>
    )
  }

  const handleClick = () => {
    if (showConsentBottomSheet && onClick) {
      // Show risk profile consent bottom sheet
      openBottomSheet(
        <RiskProfileConsentBottomSheetContent
          riskProfile={riskProfile}
          fundName={name}
          onNavigate={() => {
            closeBottomSheet()
            onClick()
          }}
          onClose={closeBottomSheet}
          throughFamilyAccess={throughFamilyAccess}
          secondaryUserName={secondaryUserName}
        />
      )
    } else if (onClick) {
      // Direct navigation
      onClick()
    }
  }

  const shouldShowTag = isWithin60Days(planStartDate)
  const hasTag = shouldShowTag

  return (
    <div onClick={handleClick} className='flex flex-row justify-center items-center w-full'>
      <div className={`${hasTag ? 'h-20' : 'h-10'} w-10 self-start ml-5 mt-4 flex-shrink-0`}>
        <img className='h-full w-full object-contain' src={`${mfLogoBaseUrl}${mfLogoUrl}`} alt='know-why' />
      </div>
      <div
        className={`flex w-full h-full pt-[18px] pb-5 items-stretch min-h-[107px] ${borderRequired && !isLast && 'border-b border-grey80'} ml-5`}
      >
        <div className='flex flex-col w-full'>
          <MutualFundRecommendationStatusTag
            recommendationStatus={recommendationStatus}
            planStartDate={planStartDate}
            recommendationStatusDisplayText={recommendationStatusDisplayText}
          />
          <div className='flex justify-between'>
            <div className='flex gap-4 w-1 flex-1'>
              <div className='flex flex-col w-full'>
                <div className='text-[13px] text-black w-10/12 leading-[140%]'>{name}</div>
                <div className='pt-[2px] text-[13px] text-[#6A6E6E]'>
                  {toPascalCaseWithSpaces(fundNature)} / {toPascalCaseWithSpaces(fundCategory)}
                </div>
              </div>
            </div>
            <div></div>
          </div>
          <div className='flex items-center gap-2 pt-4'>
            {performanceRating && performanceRating !== PowerPerformanceForms.NOT_RATED && (
              <LeafRank powerRank={powerRank} powerForm={performanceRating} isElite={true} />
            )}
            <PowerFormBadge powerForm={performanceRating} />

            {sipAmount > 0 && (
              <div className='h-[22px] text-[#6A6E6E] bg-[#F0F4F7] rounded-[8px] px-2 text-[13px] font-medium flex items-center justify-center'>
                SIP {roundOffAndFormatNumberWithCurrency(sipAmount)}
              </div>
            )}
            {performanceRating === PowerPerformanceForms.NOT_RATED && (
              <div className='h-[22px] text-grey50 bg-grey80 rounded-[8px] px-2 text-[13px] font-medium flex items-center justify-center'>
                Unrated
              </div>
            )}
            {isRegular && (
              <div className='h-[22px] text-[#992E00] bg-[#FBEFEF] rounded-[8px] px-2 text-[13px] font-medium flex items-center justify-center'>
                Regular
              </div>
            )}
          </div>
          <div className='flex flex-col items-start pt-3'>
            {highExposure && (
              <div className='h-[22px] text-orange30 rounded-[8px] text-13 font-medium flex items-center justify-center'>
                High exposure (concentration risk){' '}
                {missedGains > 0 && `and ${roundOffAndFormatNumberWithCurrency(missedGains)} missed gains`}
              </div>
            )}
            {missedGains > 0 && !highExposure && (
              <div className='h-[22px] text-orange30 rounded-[8px] text-13 font-medium flex items-center justify-center'>
                {roundOffAndFormatNumberWithCurrency(missedGains)} missed gains
              </div>
            )}
            {additionalCommissionsPaid != null && additionalCommissionsPaid > 0 && (
              <RenderHTML
                onClick={handleInfoClick}
                className="text-13 font-medium text-orange30 mr-5 cursor-pointer"
                innerHTMLText={`${roundOffAndFormatNumberWithCurrency(additionalCommissionsPaid)} paid due to high expense ${getCommissionsInfoIconHtmlAfterWord('ratio')}`}
              />
            )}
            {exclusionReason && (
              <div className='text-grey40 rounded-[8px] text-13 font-[500] flex items-center justify-center mr-5'>
                {exclusionReason}
              </div>
            )}
            {invalidationReason && (
              <div className='text-grey40 rounded-[8px] text-13 font-[500] flex items-center justify-center mr-5'>
                {invalidationReason}
              </div>
            )}
          </div>
        </div>
        {onClick && (
          <div className={`h-full flex items-center justify-center self-center`}>
            <div className='flex items-center justify-center w-10 h-10 pr-5'>
              <RightChevron />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Bottom sheet content component for excluded funds
const ExcludedFundsBottomSheetContent = ({
  fundsExcludedFromRebalancing,
  planStartDate,
  throughFamilyAccess,
  secondaryUserName
}: {
  fundsExcludedFromRebalancing: FundData[]
  planStartDate?: number
  throughFamilyAccess?: boolean
  secondaryUserName?: string
}) => {
  const { closeBottomSheet } = useBottomSheet()
  return (
    <BottomSheetContent
      bottomSheetBodyClassName='!px-0'
      headingComponent={
        <div className='flex flex-col gap-2 px-5'>
          <div>
            <WarningIcon className='h-10 w-10' />
          </div>
          <div className='text-grey10 text-[22px] font-normal mb-4'>
            {fundsExcludedFromRebalancing.length} {fundsExcludedFromRebalancing.length === 1 ? 'fund' : 'funds'} under
            review, not recommended for rebalancing yet
          </div>
        </div>
      }
      onDismiss={closeBottomSheet}
    >
      {/* <div className='relative'> */}
      {fundsExcludedFromRebalancing.map((fund, index) => {
        return (
          // <div className='border-b border-grey80'>
          <MutualFundListItem
            isLast={fundsExcludedFromRebalancing.length === 1 || index === fundsExcludedFromRebalancing.length - 1}
            key={fund.isin}
            {...fund}
            missedGains={fund.missedGains}
            highExposure={fund.highExposure}
            formType={fund.performanceRating}
            sipAmount={fund.sipAmount || 0}
            isRegular={fund.purchaseMode === 'REGULAR'}
            riskProfile={null}
            borderRequired={false}
            planStartDate={planStartDate}
            onClick={() => {
              postCTA(JSON.stringify(getMutualFundDetailCTA({ isin: fund.isin, navigationStyle: 'PUSH' })))
            }}
            throughFamilyAccess={throughFamilyAccess}
            secondaryUserName={secondaryUserName}
          />
          // </div>
        )
      })}
      {/* </div> */}
    </BottomSheetContent>
  )
}

// Bottom sheet content component for invalidated funds
const InvalidatedRebalanceFundsBottomSheetContent = ({
  invalidatedRebalanceFunds,
  navigate,
  planStartDate,
  throughFamilyAccess,
  secondaryUserName
}: {
  invalidatedRebalanceFunds: FundData[]
  navigate?: (path: string) => void
  planStartDate?: number
  throughFamilyAccess?: boolean
  secondaryUserName?: string
}) => {
  const { closeBottomSheet } = useBottomSheet()

  // Filter funds into two groups based on rebalanceSubType
  const fundsNoLongerNeedRebalancing = invalidatedRebalanceFunds.filter(
    (fund) => fund.rebalanceSubType === 'INVALIDATED_WITH_RECOMMENDATION'
  )
  const fundsNoLongerUnderReview = invalidatedRebalanceFunds.filter(
    (fund) => fund.rebalanceSubType === 'INVALIDATED_UNDER_REVIEW'
  )

  console.log(fundsNoLongerNeedRebalancing, '<<<<< fundsNoLongerNeedRebalancing')

  return (
    <BottomSheetContent
      bottomSheetBodyClassName='!px-0'
      headingComponent={
        <div className='flex flex-col gap-2 px-5'>
          <div>
            <WarningIcon className='h-10 w-10' />
          </div>
          <div className='text-grey10 text-[22px] font-normal mb-4'>
            {invalidatedRebalanceFunds.length} {invalidatedRebalanceFunds.length === 1 ? 'fund' : 'funds'} from previous
            plan no longer need any action right now
          </div>
        </div>
      }
      onDismiss={closeBottomSheet}
    >
      <div className='relative'>
        {/* Funds no longer need rebalancing section */}
        {fundsNoLongerNeedRebalancing.length > 0 && (
          <>
            {fundsNoLongerNeedRebalancing.map((fund, index) => {
              return (
                <div key={fund.isin} className='border-b border-grey80'>
                  <MutualFundListItem
                    isLast={
                      fundsNoLongerNeedRebalancing.length === 1 ||
                      (index === fundsNoLongerNeedRebalancing.length - 1 && fundsNoLongerUnderReview.length === 0)
                    }
                    {...fund}
                    missedGains={fund.missedGains}
                    highExposure={fund.highExposure}
                    formType={fund.performanceRating}
                    sipAmount={fund.sipAmount || 0}
                    isRegular={fund.purchaseMode === 'REGULAR'}
                    invalidationReason={fund.invalidationReason}
                    riskProfile={null}
                    borderRequired={false}
                    planStartDate={planStartDate}
                    onClick={() => {
                      closeBottomSheet()
                      navigate && navigate(`/rebalance-plan/${fund.isin}`)
                    }}
                    throughFamilyAccess={throughFamilyAccess}
                    secondaryUserName={secondaryUserName}
                  />
                </div>
              )
            })}
          </>
        )}

        {/* Funds no longer under review section */}
        {fundsNoLongerUnderReview.length > 0 && (
          <>
            {fundsNoLongerNeedRebalancing.length > 1 && (
              <div className='px-5 py-6'>
                <div className='text-base'>Funds no longer under review</div>
              </div>
            )}
            {fundsNoLongerUnderReview.map((fund, index) => {
              return (
                <div key={fund.isin} className='border-b border-grey80'>
                  <MutualFundListItem
                    isLast={fundsNoLongerUnderReview.length === 1 || index === fundsNoLongerUnderReview.length - 1}
                    {...fund}
                    missedGains={fund.missedGains}
                    highExposure={fund.highExposure}
                    formType={fund.performanceRating}
                    sipAmount={fund.sipAmount || 0}
                    isRegular={fund.purchaseMode === 'REGULAR'}
                    invalidationReason={fund.invalidationReason}
                    riskProfile={null}
                    borderRequired={false}
                    planStartDate={planStartDate}
                    throughFamilyAccess={throughFamilyAccess}
                    secondaryUserName={secondaryUserName}
                  />
                </div>
              )
            })}
          </>
        )}
      </div>
    </BottomSheetContent>
  )
}

const RiskProfileConsentBottomSheetContent = ({
  fundName,
  onNavigate,
  onClose,
  riskProfile,
  throughFamilyAccess,
  secondaryUserName
}: {
  fundName: string
  onNavigate: () => void
  onClose: () => void
  riskProfile: string | null
  throughFamilyAccess?: boolean
  secondaryUserName?: string
}) => {
  const showSecondary = throughFamilyAccess && secondaryUserName

  return (
    <BottomSheetContent
      headingComponent={
        <div className='flex flex-col gap-2 px-5'>
          <div>
            <WarningIcon className='h-10 w-10' />
          </div>
          <div className='text-grey10 text-[22px] font-normal mb-4'>
            This fund's category does not match{' '}
            {showSecondary ? `${getSecondaryUserName(secondaryUserName)}'s` : 'your'} risk profile :{' '}
            {toPascalCaseWithSpaces(riskProfile || '')}
          </div>
          <div className='text-grey40 text-[13px] font-normal mb-4'>
            We recommend avoiding this category as it doesn't align with{' '}
            {showSecondary ? `${getSecondaryUserName(secondaryUserName)}'s` : 'your'} risk profile. Power Allocation
            (coming soon) will guide you with the right allocation mix for {showSecondary ? `their` : 'your'} portfolio
            based on
            {showSecondary ? `their` : 'your'} risk profile.
            <br />
            <br />
            If you'd still like to view the rebalancing recommendation within the same category, please confirm below.
          </div>
        </div>
      }
      onDismiss={onClose}
      bottomSheetCtas={
        <div className='flex flex-col gap-4'>
          <PrimaryButton isFullWidth label='Confirm' onClick={onNavigate} labelClassName=''></PrimaryButton>
          <button
            className='w-full h-[56px] bg-white text-black font-medium py-3 border border-grey60 rounded-[12px]'
            onClick={onClose}
          >
            Go back
          </button>
        </div>
      }
    ></BottomSheetContent>
  )
}

// Bottom sheet content component for withdrawn funds
const WithdrawnFundsBottomSheetContent = ({
  rebalanceExecutedFunds,
  navigate,
  planStartDate,
  throughFamilyAccess,
  secondaryUserName
}: {
  rebalanceExecutedFunds: FundData[]
  navigate?: (path: string) => void
  planStartDate?: number
  throughFamilyAccess?: boolean
  secondaryUserName?: string
}) => {
  const { closeBottomSheet } = useBottomSheet()
  return (
    <BottomSheetContent
      bottomSheetBodyClassName='!px-0'
      headingComponent={
        <div className='flex flex-col gap-2 px-5'>
          <div>
            <GreenCircleTick size={40} />
          </div>
          <div className='text-grey10 text-[22px] font-normal mb-4'>
            {rebalanceExecutedFunds.length} {rebalanceExecutedFunds.length === 1 ? 'fund' : 'funds'} with suggested
            amount withdrawn
          </div>
        </div>
      }
      onDismiss={closeBottomSheet}
    >
      <div className='relative'>
        {rebalanceExecutedFunds.map((fund, index) => {
          return (
            <div key={fund.isin} className='border-b border-grey80'>
              <MutualFundListItem
                isLast={rebalanceExecutedFunds.length === 1 || index === rebalanceExecutedFunds.length - 1}
                {...fund}
                missedGains={fund.missedGains}
                highExposure={fund.highExposure}
                formType={fund.performanceRating}
                sipAmount={fund.sipAmount || 0}
                isRegular={fund.purchaseMode === 'REGULAR'}
                riskProfile={null}
                borderRequired={false}
                planStartDate={planStartDate}
                onClick={() => {
                  closeBottomSheet()
                  navigate && navigate(`/rebalance-plan/${fund.isin}`)
                }}
                throughFamilyAccess={throughFamilyAccess}
                secondaryUserName={secondaryUserName}
              />
            </div>
          )
        })}
      </div>
    </BottomSheetContent>
  )
}

export const ExcludedFundsFromRebalance = ({
  fundsExcludedFromRebalancing,
  planStartDate,
  throughFamilyAccess,
  secondaryUserName
}: {
  fundsExcludedFromRebalancing: FundData[]
  planStartDate?: number
  throughFamilyAccess?: boolean
  secondaryUserName?: string
}) => {
  const { openBottomSheet } = useBottomSheet()

  const handleClick = () => {
    openBottomSheet(
      <ExcludedFundsBottomSheetContent
        fundsExcludedFromRebalancing={fundsExcludedFromRebalancing}
        planStartDate={planStartDate}
        throughFamilyAccess={throughFamilyAccess}
        secondaryUserName={secondaryUserName}
      />
    )
  }

  return (
    <div onClick={handleClick} className='pl-5 flex flex-row w-full cursor-pointer'>
      <div className='flex flex-row gap-3 items-center'>
        <div className='w-10 h-10 flex items-center justify-center'>
          <WarningIcon className={'h-3/4 w-3/4 align-middle'} />
        </div>
      </div>
      <div className='flex flex-row gap-3 items-center flex-1 justify-between ml-5 border-b border-grey80'>
        <div className='py-4'>
          <div className='text-13 font-medium'>
            {fundsExcludedFromRebalancing.length} {fundsExcludedFromRebalancing.length === 1 ? 'fund' : 'funds'} under
            review,{' '}
          </div>
          <div className='text-13'>not recommended for rebalancing yet</div>
        </div>
        <div className='flex items-center justify-center w-10 h-full pr-5'>
          <RightChevron />
        </div>
      </div>
    </div>
  )
}

export const InvalidatedRebalanceFunds = ({
  invalidatedRebalanceFunds,
  navigate,
  planStartDate,
  throughFamilyAccess,
  secondaryUserName
}: {
  invalidatedRebalanceFunds: FundData[]
  navigate?: (path: string) => void
  planStartDate?: number
  throughFamilyAccess?: boolean
  secondaryUserName?: string
}) => {
  const { openBottomSheet } = useBottomSheet()
  const handleClick = () => {
    openBottomSheet(
      <InvalidatedRebalanceFundsBottomSheetContent
        invalidatedRebalanceFunds={invalidatedRebalanceFunds}
        navigate={navigate}
        planStartDate={planStartDate}
        throughFamilyAccess={throughFamilyAccess}
        secondaryUserName={secondaryUserName}
      />
    )
  }

  return (
    <div onClick={handleClick} className='pl-5 flex flex-row w-full cursor-pointer'>
      <div className='flex flex-row gap-3 items-center'>
        <div className='w-10 h-10 flex items-center justify-center'>
          <WarningIcon className={'h-3/4 w-3/4 align-middle'} />
        </div>
      </div>
      <div className='flex flex-row gap-3 items-center flex-1 justify-between ml-5 border-b border-grey80'>
        <div className='py-4'>
          <div className='text-13 font-medium'>
            {invalidatedRebalanceFunds.length} {invalidatedRebalanceFunds.length === 1 ? 'fund' : 'funds'} from previous
            plan no longer need any action right now
          </div>
        </div>
        <div className='flex items-center justify-center w-10 h-full pr-5'>
          <RightChevron />
        </div>
      </div>
    </div>
  )
}

export const WithdrawnFunds = ({
  rebalanceExecutedFunds,
  navigate,
  planStartDate,
  throughFamilyAccess,
  secondaryUserName
}: {
  rebalanceExecutedFunds: any
  navigate?: (path: string) => void
  planStartDate?: number
  throughFamilyAccess?: boolean
  secondaryUserName?: string
}) => {
  const { openBottomSheet } = useBottomSheet()

  const handleClick = () => {
    openBottomSheet(
      <WithdrawnFundsBottomSheetContent
        rebalanceExecutedFunds={rebalanceExecutedFunds}
        navigate={navigate}
        planStartDate={planStartDate}
        throughFamilyAccess={throughFamilyAccess}
        secondaryUserName={secondaryUserName}
      />
    )
  }

  return (
    <div onClick={handleClick} className='pl-5 flex flex-row w-full cursor-pointer'>
      <div className='flex flex-row gap-3 items-center'>
        <div className='w-10 h-10 flex items-center justify-center'>
          <GreenCircleTick size={24} className='align-middle' />
        </div>
      </div>
      <div className='flex flex-row gap-3 items-center flex-1 justify-between ml-5 border-b border-grey80'>
        <div className='py-4'>
          <div className='text-13 font-medium'>
            {rebalanceExecutedFunds?.length} {rebalanceExecutedFunds?.length === 1 ? 'fund' : 'funds'} with suggested
            amount
          </div>
          <div className='text-13'>withdrawn</div>
        </div>
        <div className='flex items-center justify-center w-10 h-full pr-5'>
          <RightChevron />
        </div>
      </div>
    </div>
  )
}

export const NoFundsToRebalance = ({
  throughFamilyAccess,
  secondaryUserName
}: {
  throughFamilyAccess?: boolean
  secondaryUserName?: string
}) => {
  const showSecondary = throughFamilyAccess && secondaryUserName
  return (
    <div className='flex flex-col px-5 pb-[112px]'>
      <img src='/images/power-rebalance/nofundstorebalance.png' className='w-[205px] h-auto' />
      <div className='text-[22px] max-w-[255px] mt-8 leading-[120%]'>
        All {showSecondary ? `${secondaryUserName}'s` : 'your'} rated funds are already in great shape
      </div>
      <div className='text-13 text-grey40 mt-4 max-w-[255px]'>
        No changes are needed right now. We review your portfolio every month to ensure it stays aligned with your goals and market conditions.
      </div>
    </div>
  )
}

export default function MutualFundListGroup({
  rebalancedFunds = [],
  fundsExcludedFromRebalancing = [],
  rebalanceExecutedFunds = [],
  invalidatedRebalanceFunds = [],
  navigate = () => { },
  riskProfile,
  planStartDate,
  throughFamilyAccess,
  secondaryUserName
}: MutualFundListGroupProps) {
  if (rebalancedFunds.length === 0) {
    return (
      <>
        <NoFundsToRebalance throughFamilyAccess={throughFamilyAccess} secondaryUserName={secondaryUserName} />
        {rebalanceExecutedFunds.length > 0 && (
          <WithdrawnFunds
            rebalanceExecutedFunds={rebalanceExecutedFunds}
            navigate={navigate}
            planStartDate={planStartDate}
            throughFamilyAccess={throughFamilyAccess}
            secondaryUserName={secondaryUserName}
          />
        )}
        {fundsExcludedFromRebalancing.length > 0 && (
          <ExcludedFundsFromRebalance
            fundsExcludedFromRebalancing={fundsExcludedFromRebalancing}
            planStartDate={planStartDate}
            throughFamilyAccess={throughFamilyAccess}
            secondaryUserName={secondaryUserName}
          />
        )}
        {invalidatedRebalanceFunds.length > 0 && (
          <InvalidatedRebalanceFunds
            invalidatedRebalanceFunds={invalidatedRebalanceFunds}
            navigate={navigate}
            planStartDate={planStartDate}
            throughFamilyAccess={throughFamilyAccess}
            secondaryUserName={secondaryUserName}
          />
        )}
      </>
    )
  } else {
    return (
      <>
        {rebalancedFunds.map((fund, index) => (
          <MutualFundListItem
            key={fund.isin}
            isin={fund.isin}
            name={fund.name}
            fundNature={fund.fundNature}
            fundCategory={fund.fundCategory}
            purchaseMode={fund.purchaseMode}
            powerRank={fund.powerRank}
            missedGains={fund.missedGains}
            performanceRating={fund.performanceRating}
            highExposure={fund.highExposure}
            mfLogoUrl={fund.mfLogoUrl}
            formType={fund.performanceRating}
            sipAmount={fund.sipAmount || 0}
            isRegular={fund.purchaseMode === 'REGULAR'}
            showConsentBottomSheet={fund.showConsentBottomSheet}
            riskProfile={riskProfile}
            recommendationStatus={fund.recommendationStatus}
            borderRequired={true}
            recommendationStatusDisplayText={fund.recommendationStatusDisplayText}
            isLast={
              (rebalancedFunds.length === 1 || index === rebalancedFunds.length - 1) &&
              !(fundsExcludedFromRebalancing.length > 0) &&
              !(invalidatedRebalanceFunds.length > 0)
            }
            onClick={() => {
              navigate(`/rebalance-plan/${fund.isin}`)
            }}
            planStartDate={planStartDate}
            throughFamilyAccess={throughFamilyAccess}
            secondaryUserName={secondaryUserName}
          />
        ))}
        {rebalanceExecutedFunds.length > 0 && (
          <WithdrawnFunds
            rebalanceExecutedFunds={rebalanceExecutedFunds}
            navigate={navigate}
            planStartDate={planStartDate}
            throughFamilyAccess={throughFamilyAccess}
            secondaryUserName={secondaryUserName}
          />
        )}
        {fundsExcludedFromRebalancing.length > 0 && (
          <ExcludedFundsFromRebalance
            fundsExcludedFromRebalancing={fundsExcludedFromRebalancing}
            planStartDate={planStartDate}
            throughFamilyAccess={throughFamilyAccess}
            secondaryUserName={secondaryUserName}
          />
        )}
        {invalidatedRebalanceFunds.length > 0 && (
          <InvalidatedRebalanceFunds
            invalidatedRebalanceFunds={invalidatedRebalanceFunds}
            navigate={navigate}
            planStartDate={planStartDate}
            throughFamilyAccess={throughFamilyAccess}
            secondaryUserName={secondaryUserName}
          />
        )}
      </>
    )
  }
}
